import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  closeCookieUI,
  getAllConsent,
  getPurposes,
  isGranted,
  localized,
  openCookieBanner,
  openCookieSettings,
  setAllConsent,
  setConsent,
  subscribeConsentUI,
  whenConsentReady,
} from '@/lib/consent';

type View = 'banner' | 'manage' | 'closed';

/**
 * Branded cookie consent UI for adventive.com.
 *
 * Zaraz decides WHO sees this (the "Consent region check" Custom HTML tool
 * calls `window.AdventiveConsent.openBanner()` for EEA/UK/CH visitors and
 * auto-grants everyone else). This component decides only how it looks and
 * writes every choice back to Zaraz, which gates the tools and sets
 * `cf_consent`. Purposes (names, descriptions, order) come from Zaraz →
 * Consent → Purposes, so wording is edited there, not here.
 */
const CookieConsent: React.FC = () => {
  const [view, setView] = useState<View>('closed');
  const [purposes, setPurposes] = useState(() => getPurposes());
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const cardRef = useRef<HTMLDivElement>(null);

  const syncFromZaraz = useCallback(() => {
    setPurposes(getPurposes());
    setChecked({ ...getAllConsent() });
  }, []);

  const open = useCallback(
    (v: Exclude<View, 'closed'>) => {
      whenConsentReady().then(() => {
        // Keep Zaraz's own modal hidden. Its setter calls hideConsentModal(), which throws
        // (removeChild on a missing node) when no default modal was ever rendered.
        try {
          if (window.zaraz?.consent) window.zaraz.consent.modal = false;
        } catch {
          /* ignore */
        }
        syncFromZaraz();
        setView(v);
      });
    },
    [syncFromZaraz],
  );

  // Bridge: footer link / other components (lib/consent.ts) and the Zaraz tool (window.AdventiveConsent)
  useEffect(() => {
    const unsub = subscribeConsentUI((v) => (v === 'closed' ? setView('closed') : open(v)));
    window.AdventiveConsent = {
      open: openCookieSettings,
      openBanner: openCookieBanner,
      close: closeCookieUI,
      granted: isGranted,
    };
    return () => {
      unsub();
      delete window.AdventiveConsent;
    };
  }, [open]);

  useEffect(() => {
    if (view !== 'closed') cardRef.current?.focus({ preventScroll: true });
  }, [view]);

  const finish = (prefs: Record<string, boolean> | boolean) => {
    if (typeof prefs === 'boolean') setAllConsent(prefs);
    else setConsent(prefs);
    setView('closed');
  };

  if (view === 'closed') return null;

  return (
    <div
      className="fixed inset-0 z-[2147483000] font-sans text-slate-900 dark:text-slate-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="adv-consent-title"
      aria-describedby="adv-consent-desc"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && view === 'manage') setView('banner');
      }}
    >
      <div className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm" />

      <div
        ref={cardRef}
        tabIndex={-1}
        className="absolute left-1/2 bottom-0 sm:bottom-6 -translate-x-1/2 w-full sm:w-[min(760px,calc(100%-32px))]
                   max-h-[88vh] overflow-auto outline-none
                   rounded-t-[20px] sm:rounded-[24px] px-5 pt-6 pb-5 sm:px-7 sm:pt-7 sm:pb-6
                   bg-white/[.98] dark:bg-[#141722]/[.96] border border-slate-900/10 dark:border-white/10
                   shadow-[0_30px_80px_rgba(0,0,0,.55),0_0_0_1px_rgba(99,91,255,.15)]
                   animate-in fade-in slide-in-from-bottom-4 duration-300"
      >
        <div className="flex items-center gap-3 mb-3">
          <span aria-hidden="true" className="w-2 h-2 rounded-full bg-[#635bff] shadow-[0_0_0_4px_rgba(99,91,255,.2)]" />
          <h2 id="adv-consent-title" className="m-0 text-lg font-extrabold tracking-tight">
            Cookies on adventive.com
          </h2>
        </div>

        <div id="adv-consent-desc" className="space-y-2.5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          <p>
            We use cookies to run this site, to understand how it is used, and to follow up when you request a demo.
            Some pages also show example ads built on the Adventive platform; those set the same cookies our ad server
            uses on our customers&rsquo; sites.
          </p>
          <p>
            Strictly necessary and functional cookies are always on. Choose what else you allow. Change your mind any
            time via <strong className="text-slate-900 dark:text-slate-100">Cookie Settings</strong> in the footer.
            Details:{' '}
            <a href="/legal/cookie-policy" className="underline underline-offset-[3px] text-slate-900 dark:text-slate-100 decoration-slate-400/60 hover:decoration-[#635bff]">
              Cookie Policy
            </a>{' '}
            &middot;{' '}
            <a href="/legal/privacy-policy" className="underline underline-offset-[3px] text-slate-900 dark:text-slate-100 decoration-slate-400/60 hover:decoration-[#635bff]">
              Privacy Policy
            </a>
            .
          </p>
        </div>

        {view === 'manage' && (
          <div className="mt-4 border-t border-slate-900/10 dark:border-white/10">
            <PurposeRow
              name="Strictly necessary & functional"
              description="Security, bot protection, your consent record, and your language choice. Cannot be switched off here."
              alwaysOn
            />
            {purposes.map((p) => (
              <PurposeRow
                key={p.id}
                name={localized(p.name)}
                description={localized(p.description)}
                checked={checked[p.id] === true}
                onChange={(v) => setChecked((c) => ({ ...c, [p.id]: v }))}
              />
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2.5">
          <Btn variant="primary" onClick={() => finish(true)}>Accept all</Btn>
          <Btn variant="secondary" onClick={() => finish(false)}>Reject all</Btn>
          {view === 'manage' ? (
            <Btn variant="secondary" onClick={() => finish(checked)}>Save choices</Btn>
          ) : (
            <button
              type="button"
              onClick={() => setView('manage')}
              className="ml-auto basis-full sm:basis-auto text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white underline underline-offset-[3px] px-2 py-3 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#635bff]"
            >
              Manage choices
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Btn: React.FC<{ variant: 'primary' | 'secondary'; onClick: () => void; children: React.ReactNode }> = ({ variant, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 sm:flex-none text-sm font-semibold rounded-full px-[22px] py-3 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#635bff] ${
      variant === 'primary'
        ? 'bg-[#635bff] text-white hover:bg-[#7a73ff]'
        : 'bg-slate-900/[.06] hover:bg-slate-900/[.12] text-slate-900 dark:bg-white/[.08] dark:hover:bg-white/[.14] dark:text-slate-50'
    }`}
  >
    {children}
  </button>
);

const PurposeRow: React.FC<{
  name: string;
  description: string;
  alwaysOn?: boolean;
  checked?: boolean;
  onChange?: (v: boolean) => void;
}> = ({ name, description, alwaysOn, checked, onChange }) => (
  <div className="flex items-start gap-4 py-3.5 border-b border-slate-900/10 dark:border-white/10 last:border-b-0">
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold mb-0.5">{name}</div>
      <p className="m-0 text-[13px] leading-relaxed text-slate-600 dark:text-slate-400">{description}</p>
    </div>
    {alwaysOn ? (
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap pt-1">Always on</span>
    ) : (
      <label className="relative shrink-0 w-11 h-[26px] mt-0.5 cursor-pointer">
        <input
          type="checkbox"
          className="peer absolute inset-0 w-full h-full m-0 opacity-0 cursor-pointer"
          checked={!!checked}
          onChange={(e) => onChange?.(e.target.checked)}
          aria-label={name}
        />
        <span
          className="absolute inset-0 rounded-full bg-slate-900/15 dark:bg-white/15 transition-colors peer-checked:bg-[#635bff]
                     peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#635bff]
                     after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:w-5 after:h-5 after:rounded-full after:bg-white after:transition-transform
                     peer-checked:after:translate-x-[18px]"
        />
      </label>
    )}
  </div>
);

export default CookieConsent;
